import React, {
	type Context,
	createContext,
	type MutableRefObject,
	type ReactNode,
	useContext,
	useRef,
} from 'react';

export type TaskState = {
	id: string | null;
	state: 'idle' | 'started' | 'completed' | 'failed' | 'aborted';
	error: Error | null;
	queryHash: string | null;
	queryLength: number | null;
	wordCount: number | null;
};

// Default value hoisted outside to prevent recreation on every render
const DEFAULT_TASK_STATE: TaskState = {
	id: null,
	state: 'idle',
	error: null,
	queryHash: null,
	queryLength: null,
	wordCount: null,
};

const IntentDetectionVitalStatsContext: Context<MutableRefObject<TaskState> | null> =
	createContext<React.MutableRefObject<TaskState> | null>(null);

/**
 * Provider component that manages VitalStats task state for intent detection UX tracking.
 * This provider should wrap the QuickFind component tree to enable VitalStats tracking
 * for the intent detection UX task lifecycle.
 *
 * Uses a shared ref to avoid recreating state on every render and prevent unnecessary rerenders.
 *
 * @example
 * ```tsx
 * <IntentDetectionVitalStatsProvider>
 *   <QuickFindComponent />
 * </IntentDetectionVitalStatsProvider>
 * ```
 */
export const IntentDetectionVitalStatsProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	// Use hoisted default to prevent object recreation on every render
	const taskStateRef = useRef<TaskState>(DEFAULT_TASK_STATE);

	return (
		<IntentDetectionVitalStatsContext.Provider value={taskStateRef}>
			{children}
		</IntentDetectionVitalStatsContext.Provider>
	);
};

/**
 * Hook to access the shared intent detection VitalStats task state ref.
 * Returns a stable ref that won't cause rerenders when mutated.
 */
export const useIntentDetectionVitalStatsTaskState = (): MutableRefObject<TaskState> => {
	const taskStateRef = useContext(IntentDetectionVitalStatsContext);
	if (!taskStateRef) {
		throw new Error(
			'useIntentDetectionVitalStatsTaskState must be used within IntentDetectionVitalStatsProvider',
		);
	}
	return taskStateRef;
};
